import { useQuery } from '@tanstack/react-query';
import type { LiveCallCountResponse, LiveCallListResponse } from '@vagusx/clients';
import { getInteractionClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { POLL_TIME_LIVE } from '@/lib/query-client';
import { useLocation } from '@tanstack/react-router';
import { liveCountPollInterval, liveCountToShow } from '@/lib/live-count';
import { useHasPermission } from '@/hooks/use-permissions';
import { P } from '@vagusx/shared';

/**
 * The roster of voice calls on the line right now.
 *
 * Polled rather than streamed. There is a per-interaction SSE endpoint
 * (`useLiveTranscript`) but no org-wide one, and a roster that changes when
 * a call starts or ends — not on every utterance — does not justify
 * building one. `refetchIntervalInBackground` is deliberately left off: a
 * hidden tab polling every five seconds is cost with no viewer.
 *
 * `elapsedSeconds` on each row is a SERVER reading, taken when the response
 * was built. Anything rendering a running clock should count up from it
 * locally rather than waiting for the next poll, or the timers visibly jump
 * in five-second steps.
 */
export function useLiveCalls(options?: { enabled?: boolean }) {
  return useQuery<LiveCallListResponse>({
    queryKey: queryKeys.interactions.live(),
    queryFn: () => getInteractionClient().listLive(),
    refetchInterval: POLL_TIME_LIVE,
    staleTime: POLL_TIME_LIVE,
    enabled: options?.enabled ?? true,
  });
}

/**
 * How many calls are on the line right now, ready to display: 0 means show
 * nothing (none live, not known yet, or the last poll failed — see
 * `liveCountToShow`).
 *
 * Read by the sidebar's Calls badge, which is on every page, and the Live
 * Calls button on the Calls page; one query key, so they share one cache
 * entry. ONE OBSERVER POLLS (`poll: true`, the sidebar): React Query gives
 * each observer its own interval timer and dedupes only in-flight fetches,
 * so two polling observers would fetch about twice per interval. The poller
 * runs at the live cadence on `/calls` pages and slower elsewhere
 * (`liveCountPollInterval`); a `poll: false` reader still refetches on
 * mount once the entry is stale. A count query, not `useLiveCalls`: polling the full roster would
 * pull every live call's patient and last transcript line every few seconds
 * just to read `.total`, and that total stops at the roster cap.
 *
 * Gated on `PATIENTS_VIEW`, the endpoint's own permission, so a user who
 * cannot read calls never polls into a 403.
 */
export function useLiveCallCount({ poll }: { poll: boolean }): number {
  const canReadCalls = useHasPermission(P.PATIENTS_VIEW);
  const pathname = useLocation({ select: (location) => location.pathname });
  const query = useQuery<LiveCallCountResponse>({
    queryKey: queryKeys.interactions.liveCount(),
    queryFn: () => getInteractionClient().countLive(),
    refetchInterval: poll ? liveCountPollInterval(pathname) : false,
    staleTime: POLL_TIME_LIVE,
    enabled: canReadCalls,
  });
  return liveCountToShow(query);
}
